import { useEffect, useRef, useState } from 'react'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useAddNotification } from 'hooks/useAddNotification'
import { get, post } from 'utils/http'

const getDisplayName = (params: any) => params.company_name || params.company_domain

/**
 * Manages AI company enrichment for the management company detail page (scoped to one company).
 *
 * `companyName` state doubles as a notification gate (only shown when set) and the display name.
 * Status polling always runs on mount (enabled: !!companyUuid) so the button reflects an
 * enrichment started in a previous session; when one is detected on mount (companyName null),
 * companyName is seeded from `initialCompanyName` to activate notifications.
 * `onComplete` (via ref for stable identity) lets the parent refresh the data-change-requests list on finish.
 */
export const useCompanyAiEnrichment = (
  companyUuid: string,
  {
    onComplete,
    companyName: initialCompanyName,
  }: {
    onComplete?: (...args: any[]) => any
    companyName?: string
  } = {},
) => {
  const queryClient = useQueryClient()
  const addNotification = useAddNotification()
  const [companyName, setCompanyName] = useState<string | null>(null)
  const onCompleteRef = useRef<((...args: any[]) => any) | undefined>(onComplete)
  onCompleteRef.current = onComplete

  const { mutate: trigger, isPending: isTriggering } = useMutation({
    mutationFn: (params: any) => post(`/management/companies/${companyUuid}/ai-enrichment`, params),
    onMutate: (params: any) => {
      setCompanyName(getDisplayName(params))
    },
    onSuccess: (_data: any, params: any) => {
      addNotification(`AI enrichment started for ${getDisplayName(params)}`)
      // Reset query cache so polling starts fresh from the new enrichment
      queryClient.resetQueries({ queryKey: ['ai-enrichment-status', companyUuid] })
    },
    onError: (error: any, params: any) => {
      const name = getDisplayName(params)
      if (error.status === 409) {
        // Already in progress — still start polling so user gets notified on completion
        addNotification(`Enrichment already in progress for ${name}`, 'error')
        queryClient.resetQueries({ queryKey: ['ai-enrichment-status', companyUuid] })
      } else {
        addNotification(`Failed to start AI enrichment for ${name}`, 'error')
        setCompanyName(null)
      }
    },
  })

  // Polls status every 10s while in_progress; always enabled on mount to catch pre-session enrichments.
  const { data: statusData } = useQuery({
    queryKey: ['ai-enrichment-status', companyUuid],
    queryFn: () => get(`/management/companies/${companyUuid}/ai-enrichment/status`),
    enabled: !!companyUuid,
    // v5: refetchInterval's callback receives the Query, not the data (read via query.state.data).
    refetchInterval: query => (query.state.data?.status === 'in_progress' ? 10_000 : false),
    refetchOnWindowFocus: false,
  })

  const isCompanyEnriching = statusData?.status === 'in_progress'

  // Toasts on completion/failure; when in_progress is detected on mount (companyName null), seeds companyName from initialCompanyName to activate notifications.
  useEffect(() => {
    if (!statusData) return

    if (statusData.status === 'in_progress' && !companyName) {
      setCompanyName(initialCompanyName || 'this company')
      return
    }

    if (!companyName) return

    if (statusData.status === 'completed') {
      addNotification(`AI enrichment complete for ${companyName}. A data change request has been created.`)
      setCompanyName(null)
      onCompleteRef.current?.()
    } else if (statusData.status === 'failed') {
      addNotification(`AI enrichment failed for ${companyName}: ${statusData.error || 'Unknown error'}`, 'error')
      setCompanyName(null)
    }
  }, [statusData, companyName, addNotification])

  return { trigger, isCompanyEnriching, isTriggering }
}
